import React, { useContext, useState, useCallback } from 'react';
import { supportedLangsList } from 'i18n';
import { useId } from '@fluentui/react-hooks';
import { NeutralColors } from '@fluentui/theme';
import { Modal, IModalStyles, PrimaryButton, DefaultButton, mergeStyleSets, FontWeights, FontSizes } from '@fluentui/react';

import { getIconSvg } from 'utils/svg';
import { downToLargeScreenSelector } from 'utils/breakpoints';
import TranslationsContext from './TranslationsContext';
import translationKeys from 'translationKeys';

interface LanguageSelectorModalProps {
	isOpen: boolean;
	onDismiss: () => void;
}

const styles = mergeStyleSets({
	root: {
		padding: ' 1rem 1rem',
		margin: '1.25rem',
		maxWidth:'280px',
		width: '100%',
		height: 'auto',
		display: 'flex',
		background: NeutralColors.gray20,
		[downToLargeScreenSelector]: {
			height: '380px',
			maxWidth: '600px',
			width: '100%',
			padding: ' 1rem 1.25rem',
		}
	},
	main: {
		flexGrow: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-end',
		justifyContent: 'space-between',
	},
	heading: {
		fontSize: FontSizes.size20,
		fontWeight: FontWeights.semibold,
		marginBottom: '2rem'
	},
	mainContent: {
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		alinItems: 'stretch',
		marginBottom: '3rem'
	},
	languageSelectorContainer: {
		width: '50%',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		alignItems: 'stretch',
		overflow: 'auto',
		maxHeight: '200px',
		minHeight: 'min-content',
		marginTop: 'auto',
		marginBottom: 'auto'
	},
	languageButton: {
		padding: '1.25rem',
		textAlign: 'left',
		'&:not(:last-child)': {
			marginBottom: '0.25rem'
		}
	},
	icon: {
		width: '100%',
		height: 'auto',
		maxWidth: '8rem',
		maxHeight: '8rem'
	},
	iconContainer: {
		display: 'none',
		width: '50%',
		justifyContent: 'center',
		alignItems: 'center',
		[downToLargeScreenSelector]: {
			display: 'flex',
		}
	}
});

const modalStyles: Partial<IModalStyles> = {
	scrollableContent: {
		display: 'flex',
		flexGrow: 1
	}
};

const modalSvgSrc = getIconSvg('locale', 'images');

export const LanguageSelectorModal = ({ isOpen, onDismiss }: LanguageSelectorModalProps) => {
	const modalTitleId = useId('modalTitle');
	const { selectedLangCode, setSelectedLangCode, t } = useContext(TranslationsContext)!;
	const [ UIselectedLangCode, setUIselectedLangCode ] = useState(selectedLangCode);

	const renderLanguageList = useCallback(() => {
		return supportedLangsList.map(lang => {
			const isSelected = UIselectedLangCode === lang.code;
			const buttonTextKey = `${UIselectedLangCode}Name`;
			const ButtonComponent = isSelected ? PrimaryButton : DefaultButton;

			return <ButtonComponent
				role="option"
				aria-selected={isSelected}
				key={lang.code}
				text={lang[buttonTextKey]}
				className={styles.languageButton}
				onClick={() => setUIselectedLangCode(lang.code)}
			/>;
		});
	}, [UIselectedLangCode]);

	return <Modal isOpen={isOpen}
		onDismiss={onDismiss}
		containerClassName={styles.root}
		titleAriaId={modalTitleId}
		styles={modalStyles}
	>
		<div className={styles.main}>
			<h2 id={modalTitleId} className={styles.heading}>{t(translationKeys.Layout.LanguageSelector.Title)}</h2>
			<div className={styles.mainContent}>
				<div className={styles.iconContainer}><img className={styles.icon} src={modalSvgSrc} alt={t(translationKeys.Layout.LanguageSelector.Icon.Alt)} /></div>
				<div role="listbox" aria-labelledby={modalTitleId} aria-multiselectable={false} className={styles.languageSelectorContainer}>
					{ renderLanguageList() }
				</div>
			</div>
			<PrimaryButton text={t(translationKeys.Layout.LanguageSelector.Button)} onClick={() => setSelectedLangCode(UIselectedLangCode)}/>
		</div>
	</Modal>;
};
